import { useSyncExternalStore } from 'react';

/**
 * THE READER'S TRANSPARENCY OVERRIDE, held in one place because the panels share one surface.
 *
 * <h2>Why a store and not a prop</h2>
 *
 * Four surfaces read {@link glassSurface}: the ledger, the engine state, the position HUD and the
 * transport. Three of them live in one component file and one in another, and two are rendered
 * from different parents — so threading a prop would mean four signatures changed and four
 * chances for one of them to be forgotten. A panel left behind at the default would be the exact
 * divergence `glassSurface.js` exists to prevent, arriving by a new route: one dock, two
 * materials.
 *
 * <p>So the preference is subscribed to rather than passed down. `useSyncExternalStore` is the
 * supported way to read external state in React 18 — it keeps every subscriber on one value in
 * the same render pass, where a `useState` per panel plus an event listener would let them
 * disagree for a frame.
 *
 * <h2>It is PERSISTED, because it is a reading preference and not a session setting</h2>
 *
 * A reader who finds the dense panel hard to see through on their monitor should not have to say
 * so again on the next session. Stored under one key, read once at module load, and written on
 * every change.
 *
 * <p>Every `localStorage` touch is wrapped: Safari in private mode throws on `setItem`, and a
 * panel that cannot remember an opacity must still be able to change one. A failure here costs
 * the persistence and nothing else.
 */

/** The one key. Namespaced like the toolbar's own preference, so it is findable in devtools. */
export const GLASS_TRANSPARENCY_KEY = 'simulator_glass_high_transparency';

const read = () => {
  try {
    return window.localStorage.getItem(GLASS_TRANSPARENCY_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

let value = read();
const listeners = new Set();

/** The current preference. OFF means the measured-legible default fill. */
export const isHighTransparency = () => value;

/**
 * Set it, persist it, and tell every panel at once.
 *
 * <p>A no-op when the value has not changed, so a caller that re-sets the same value does not
 * re-render four panels for nothing.
 */
export function setHighTransparency(next) {
  const wanted = next === true;
  if (wanted === value) return;
  value = wanted;
  try {
    window.localStorage.setItem(GLASS_TRANSPARENCY_KEY, String(wanted));
  } catch (e) {
    // The preference still applies to this session; only remembering it failed.
  }
  listeners.forEach((fn) => fn());
}

/** Flip it. What the panel's own button calls. */
export const toggleHighTransparency = () => setHighTransparency(!value);

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/** Re-reads `localStorage`. For tests, which need a known starting point per case. */
export function resetHighTransparencyForTest() {
  value = read();
  listeners.forEach((fn) => fn());
}

/** Subscribe a component to the preference. */
export function useHighTransparency() {
  return useSyncExternalStore(subscribe, isHighTransparency, isHighTransparency);
}

export default useHighTransparency;
